// FILECONTENT_START src/common/components/DataGrid/DataGridToolbarActions.tsx
import { alpha } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { createStyles, makeStyles } from "@mui/styles";
import Spacer from "../Spacer";
import DataGridFilterIndicator from "./DataGridFilterIndicator";
import { DataGridToolbarProps } from "./DataGridToolbar";
import DataGridToolbarQuickSearch from "./DataGridToolbarQuickSearch";
import {
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "./grid-imports";
import DataGridToolbarAddButton from "./toolbar-buttons/DataGridToolbarAddButton";
import DataGridToolbarCustomFilterButton from "./toolbar-buttons/DataGridToolbarCustomFilterButton";
import DataGridToolbarCustomSettingsButton from "./toolbar-buttons/DataGridToolbarCustomSettingsButton";
import DataGridToolbarDeleteSelectedButton from "./toolbar-buttons/DataGridToolbarDeleteSelectedButton";
import DataGridToolbarRefreshButton from "./toolbar-buttons/DataGridToolbarRefreshButton";
import DataGridToolbarRestoreSelectedButton from "./toolbar-buttons/DataGridToolbarRestoreSelectedButton";
import DataGridToolbarToggleTrashButton from "./toolbar-buttons/DataGridToolbarToggleTrashButton";
import DataGridToolbarWrapTextButton from "./toolbar-buttons/DataGridToolbarWrapTextButton";

const defaultTheme = createTheme();
const useStyles = makeStyles(
  (theme: any) =>
    createStyles({
      row: {
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap",
      },
      root: {
        padding: theme.spacing(0.5, 0.5, 0),
        justifyContent: "space-between",
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap",
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
      },
      rootOnlyTrashed: {
        padding: theme.spacing(0.5, 0.5, 0),
        justifyContent: "space-between",
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap",
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        backgroundColor: alpha(theme.palette.warning.main, 0.2),
      },
      textField: {
        width: "auto",
        [theme.breakpoints.down("xs")]: {
          width: "100%",
        },
        margin: theme.spacing(1, 0.5, 1.5),
        "& .MuiSvgIcon-root": {
          marginRight: theme.spacing(0.5),
        },
        "& .MuiInput-underline:before": {
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
    }),
  { defaultTheme }
);

interface DataGridToolbarActionsProps extends DataGridToolbarProps {}

export default function DataGridToolbarActions(
  props: DataGridToolbarActionsProps
) {
  const {
    canCreate,
    canBatchActions,
    canHandleTrash,
    deleteSelectedItems,
    restoreSelectedItems,
    headerItems = [],
    selectionState,
    trashType,
    extraHeaderItem,
    rowsState,
    filter,
    applyFilter,
    autoColorFilterIndicator,
    translationCategory,
    options,
    headerTitle,
    attributesNotCount,
    renderFilterIndicatorItem,
  } = props;

  return (
    <>
      {!!headerTitle && headerTitle(rowsState)}
      {!!deleteSelectedItems &&
        selectionState &&
        selectionState.length > 0 &&
        canBatchActions && <DataGridToolbarDeleteSelectedButton {...props} />}
      {!!restoreSelectedItems &&
        canBatchActions &&
        trashType === "onlyTrashed" &&
        selectionState &&
        selectionState.length > 0 && (
          <DataGridToolbarRestoreSelectedButton {...props} />
        )}
      {canCreate && trashType !== "onlyTrashed" && (
        <DataGridToolbarAddButton {...props} />
      )}
      {headerItems.includes("filter") && <GridToolbarFilterButton />}
      {headerItems.includes("density") && <GridToolbarDensitySelector />}
      {headerItems.includes("filterIndicator") && (
        <DataGridFilterIndicator
          style={{ flex: 1 }}
          autoColor={autoColorFilterIndicator}
          translationCategory={translationCategory}
          item={filter}
          applyFilter={applyFilter}
          options={options}
          attributesNotCount={attributesNotCount}
          renderItem={renderFilterIndicatorItem}
        />
      )}
      <Spacer right />
      {!!extraHeaderItem &&
        extraHeaderItem(rowsState, { ...filter, options: props.settings })}
      <DataGridToolbarWrapTextButton {...props} />
      {canHandleTrash && <DataGridToolbarToggleTrashButton {...props} />}
      {headerItems.includes("refresh") && (
        <DataGridToolbarRefreshButton {...props} />
      )}
      {headerItems.includes("customSettings") && (
        <DataGridToolbarCustomSettingsButton {...props} />
      )}
      {headerItems.includes("customFilter") && (
        <DataGridToolbarCustomFilterButton {...props} />
      )}
      {headerItems.includes("search") && (
        <DataGridToolbarQuickSearch {...props} />
      )}
    </>
  );
}

// FILECONTENT_END src/common/components/DataGrid/DataGridToolbarActions.tsx